import React, { useState, useEffect, useCallback } from 'react';
import { sectionMasterItemsService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Trash2, Pencil, X, Check, ListChecks } from 'lucide-react';
import { getApiError } from '@/lib/utils';

/**
 * Shared Allowances/Management/Adjustment master-list CRUD content.
 * Used both by the full-page route (SectionMasterDataPage) and the
 * in-wizard "Manage list" modal (ManageMasterListDialog), so both
 * surfaces have identical functionality.
 */
export default function SectionMasterDataManager({ category, title, onItemsChanged }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const testSlug = category;

  const fetchItems = useCallback(async () => {
    // Guard: the in-wizard dialog sets category to null right as it starts
    // closing (while still mounted for the close animation) — skip the fetch
    // rather than calling the API with a missing category.
    if (!category) return;
    setLoading(true);
    try {
      const data = await sectionMasterItemsService.list(category);
      setItems(data || []);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to load list'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [category, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const notifyChanged = () => { if (onItemsChanged) onItemsChanged(); };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await sectionMasterItemsService.create(category, newName.trim());
      setNewName('');
      toast({ title: 'Added', description: `"${newName.trim()}" added to ${title}` });
      await fetchItems();
      notifyChanged();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to add'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveEdit = async () => {
    if (!editingName.trim()) return;
    setSaving(true);
    try {
      await sectionMasterItemsService.update(editingId, { name: editingName.trim() });
      toast({ title: 'Updated' });
      cancelEdit();
      await fetchItems();
      notifyChanged();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to update'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await sectionMasterItemsService.update(item.id, { is_active: !item.is_active });
      await fetchItems();
      notifyChanged();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to update'), variant: 'destructive' });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await sectionMasterItemsService.remove(id);
      toast({ title: 'Deleted', description: `"${name}" removed` });
      await fetchItems();
      notifyChanged();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testSlug}-master-manager`}>
      <form onSubmit={handleAdd} className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={`Add a new ${title.toLowerCase()} name...`}
          className="max-w-md"
          data-testid={`${testSlug}-new-name-input`}
        />
        <Button type="submit" disabled={!newName.trim() || saving} data-testid={`${testSlug}-add-btn`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          Add
        </Button>
        <Badge variant="outline" className="whitespace-nowrap ml-auto">
          {items.length} item{items.length !== 1 ? 's' : ''}
        </Badge>
      </form>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ListChecks className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No {title.toLowerCase()} names yet. Add your first one above.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-center w-28">Active</TableHead>
                <TableHead className="text-right w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`${testSlug}-row-${item.id}`}>
                  <TableCell className="font-medium">
                    {editingId === item.id ? (
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="h-8 max-w-sm"
                        autoFocus
                        data-testid={`${testSlug}-edit-input-${item.id}`}
                      />
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={item.is_active}
                      onCheckedChange={() => toggleActive(item)}
                      data-testid={`${testSlug}-active-toggle-${item.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {editingId === item.id ? (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={saveEdit}
                          disabled={saving} data-testid={`${testSlug}-save-edit-${item.id}`}>
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={cancelEdit}
                          data-testid={`${testSlug}-cancel-edit-${item.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(item)}
                          data-testid={`${testSlug}-edit-${item.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                          onClick={() => handleDelete(item.id, item.name)} disabled={deletingId === item.id}
                          data-testid={`${testSlug}-delete-${item.id}`}>
                          {deletingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
